import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { PnlShareModal } from '@flipster/features/src/shared/pnl-share-modal';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useOverlay } from '@flipster/overlay';
import { spotMarketsBunja } from '@flipster/r230522/original/spot-market';
import { getSpotTickerDisplayPrice } from '@flipster/r230522/original/ticker-price';
import { Wallet } from '@flipster/r230522/original/wallet';
import Formatted from '@flipster/react/design-utils/formatted';
import Share2Icon from '@flipster/react/icon/generated/Share2Icon';
import { css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';
import { Price } from '@flipster/ui';
import { TableCell, TableRow } from '@flipster/ui/Table';
import { AssetPrecision, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';

import styles from './positionTable.module.scss';

interface Props {
  item: Wallet & { currency: string };
  showPnl: boolean;
}

const PurchasedPositionRow = ({ item, showPnl }: Props) => {
  const { spotMarketsAtom } = useBunja(spotMarketsBunja);
  const spotMarket = useAtomValue(spotMarketsAtom);

  const overlay = useOverlay();
  const { spotInstruments } = useSnapshotSpec();

  const spotSymbolInfo = spotInstruments?.find((value) => value.baseCurrency === item.currency);

  return (
    <TableRow>
      <TableCell>
        <CurrencyDisplay
          currency={item.currency}
          icon={<CurrencyDisplay.Icon size={16} />}
          mainLabel={
            <CurrencyDisplay.Label
              value='shortName'
              css={{ textStyle: 'Text/S/Strong', color: 'content.default.level-5' }}
            />
          }
        />
      </TableCell>
      <TableCell>
        <div className={styles.multiLine}>
          <Price
            price={item.walletBalanceExclWithdrawalPending}
            fontType='num4'
            precision={AssetPrecision}
            toFixed
            abs
          />
          <Price
            className={styles.size}
            price={item.walletBalanceExclWithdrawalPendingInUsdt}
            fontType='num4'
            precision={UsdtPrecision}
            currency={UsdtCurrency}
            toFixed
            abs
          />
        </div>
      </TableCell>
      {showPnl && (
        <TableCell>
          <div className={styles.multiLine}>
            <Price price={item.pnl} fontType='num4' precision={UsdtPrecision} toFixed withColor />
            <Formatted.Percent
              value={item.pnlPct}
              withColor
              precision={UsdtPrecision}
              toFixed
              withPlus
              withParentheses
              css={{ textStyle: 'Legacy/Number4' }}
            />
          </div>
          <button
            type='button'
            className={css({ ml: 4, p: '0px!' })}
            onClick={() => {
              if (!spotSymbolInfo) return;
              if (!item.pnl || !item.pnlPct) return;
              const currentSpot = spotMarket?.[spotSymbolInfo.symbol];
              if (!currentSpot) return;
              const displayPrice = getSpotTickerDisplayPrice(currentSpot);
              if (!displayPrice) return;
              overlay.open(({ isOpen, close, unmount }) => (
                <PnlShareModal
                  isOpen={isOpen}
                  onClose={close}
                  position={{
                    type: 'spot',
                    status: 'open',
                    symbol: spotSymbolInfo.symbol,
                    entryPrice: item.avgEntryPrice ?? '0',
                    evaluationPrice: displayPrice,
                    pnl: item.pnl ?? '0',
                    pnlPercentage: item.pnlPct ?? '0',
                  }}
                  onExit={unmount}
                />
              ));
              segment.track(PositionEvent.PnLShareClick, {
                position_status: 'open',
                position: null,
                leverage: 1,
                symbol: spotSymbolInfo.symbol,
                unrealized_pnl: item.pnl,
                unrealized_pnl_percentage: item.pnlPct,
                current_price: displayPrice,
                trade_type: 'spot',
                currency: item.currency,
              });
            }}
          >
            <Share2Icon size={16} color={token('colors.content.default.level-2')} />
          </button>
        </TableCell>
      )}
    </TableRow>
  );
};

export default PurchasedPositionRow;
